import React, { useState } from 'react';

import chevronDown from '@/public/images/icons/chevron-down.svg';

type Local = 'en' | 'fr';

interface ClientHubFaqProps {
  isMobile: boolean;
  activeSection: string;
  track: any;
  scrollTo: any;
  sitelinkId: string;
  local: Local;
}

const translations: Record<Local, any> = {
  en: {
    faqTitle: 'FAQs',
    multipleChargesQuestion: 'Why do I see multiple rent charges on my bill?',
    multipleChargesAnswer: (
      <>
        Your account is billed every 4 weeks from your move-in date. You may see
        upcoming rent cycles listed on your statement — this is normal and meant
        for visibility. <br />
        Important: You only need to pay for your current billing cycle. Any{' '}
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        future rent charges shown are not yet due. If you'd like to pay ahead,{' '}
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        you're welcome to — but it's not required.
      </>
    ),
    accessQuestion: 'When may I access my unit?',
    accessAnswer: (scrollTo: (sectionId: string) => void) => (
      <>
        Gate hours can be found in the{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            scrollTo('contact');
            return false;
          }}
          style={{ textDecoration: 'none' }}
        >
          Contact
        </a>{' '}
        section.
      </>
    ),
    storageTipsQuestion: 'Storage tips',
    storageTipsAnswer: (
      <>
        Here are some best practices to help you store with confidence:
        <ul>
          <li>Label your boxes clearly to make items easy to find.</li>
          <li>
            Cover mattresses and soft goods with plastic to protect from dust
            and moisture.
          </li>
          <li>Keep your unit secure—never share your access code or key.</li>
          <li>
            Make an inventory list and store it in a safe place for easy
            reference.
          </li>
          <li>
            Keep items off the floor when possible by using pallets or
            protective mats.
          </li>
        </ul>
      </>
    ),
    multipleUnitsQuestion: 'What if I have more than one unit?',
    multipleUnitsAnswer: (
      sitelinkId: string,
      track: (eventName: string) => void
    ) => (
      <>
        If you have more than one unit with us, you can see and manage all your
        units in one place.
        <br />
        <br />
        Visit our secure client portal:{' '}
        <a
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noreferrer"
          onClick={() => track('Open MyAccount')}
          href={`https://www.smdservers.net/SLWebSiteTemplate_V2/login.aspx?sCorpCode=ilv2Y31A9yHEzplusa5/gLLlA==&sLocationCode=${sitelinkId}==&1=1`}
        >
          My Account
        </a>
        <br />
        Once logged in:
        <ol>
          <li>
            A dropdown menu with the list of all your units will be available
          </li>
          <li>Select one of your units</li>
          <li>
            From this point forward, follow the same steps outlined in the
            Payments and/or Access section
          </li>
        </ol>
      </>
    ),
    referralsQuestion: 'Referrals',
    referralsAnswer: (
      <>
        Send someone our way and get up to $100 through our referral program. It
        pays to be a good friend!
        <ul>
          <li>
            Eligibility: both current and past clients are eligible to
            participate.
          </li>
          <li>
            Once your friend rents the unit you will be contacted to receive
            your reward – usually in just a few days.
          </li>
          <li>
            Rewards will be issued by prepaid credit card after the referral has
            moved into the unit and submitted all required documentation.
          </li>
          <li>
            Reward amount:
            <br />
            <table border={1} cellPadding={6} cellSpacing={0}>
              <thead>
                <tr>
                  <th style={{ minWidth: '4em' }}>Amount</th>
                  <th>Unit size</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>$50</td>
                  <td>0–100 sqft</td>
                </tr>
                <tr>
                  <td>$75</td>
                  <td>101–250 sqft</td>
                </tr>
                <tr>
                  <td>$100</td>
                  <td>Larger than 250 sqft</td>
                </tr>
              </tbody>
            </table>
          </li>
        </ul>
      </>
    ),
    movingOutQuestion: 'Moving out',
    movingOutAnswer: (
      <>
        Your rental is fully flexible!
        <br />
        If you only need the unit for part of your next cycle, just let us know
        one week before the start of the next billing cycle. We’ll gladly
        provide a one-time, pro-rated rent invoice.
        <br />
        Refunds are available only if you’ve moved out before the rental’s due
        date.
        <br />
        Please take all items with you at move-out. A disposal fee of $50 to
        $500 will apply if items are left behind after move-out.
      </>
    ),
    protectionPlanQuestion: 'Protection Plan details',
    protectionPlanAnswer: (
      <>
        Peace of mind comes standard!
        <br />
        All rentals include automatic enrollment in our Protection Plan.
        <br />
        This plan offers limited coverage for specific risks like fire, theft,
        and certain types of water damage.
        <br />
        If you have your own insurance, simply share proof with us to opt out.
        <ul>
          <li>Coverage limits range from $2,000 to $5,000</li>
          <li>
            Some exclusions apply (e.g., mold, vermin, perishables, cash or
            jewelry)
          </li>
          <li>Claims must be submitted promptly with documentation</li>
          <li>
            This plan is not a replacement for personal home or renter’s
            insurance
          </li>
          <li>
            Review full details here:{' '}
            <a
              style={{ textDecoration: 'none' }}
              target="_blank"
              rel="noreferrer"
              onClick={() => translations.en.track('Open terms of service')}
              href="https://www.depotium.com/en/terms-of-service/"
            >
              Terms of Service
            </a>
          </li>
        </ul>
      </>
    ),
    latePaymentQuestion: 'Late payment process',
    latePaymentAnswer: (
      sitelinkId: string,
      track: (eventName: string) => void
    ) => (
      <>
        The best way to keep your account current is to enroll in pre-authorized
        payment. You can sign up via our secure client portal:
        <a
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noreferrer"
          onClick={() => track('Open MyAccount')}
          href={`https://www.smdservers.net/SLWebSiteTemplate_V2/login.aspx?sCorpCode=ilv2Y31A9yHEzplusa5/gLLlA==&sLocationCode=${sitelinkId}==&1=1`}
        >
          My Account
        </a>
        .
        <br />
        <table border={1} cellPadding={6} cellSpacing={0}>
          <thead>
            <tr>
              <th style={{ minWidth: '4em' }}>Day</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Day 3</td>
              <td>A late fee of 5% (minimum $5) is added to the account.</td>
            </tr>
            <tr>
              <td>Day 5</td>
              <td>
                Access is temporarily paused to the unit and the facility.
              </td>
            </tr>
            <tr>
              <td>Day 9</td>
              <td>
                An additional late fee of 5% (minimum $5) is added to the
                account.
              </td>
            </tr>
            <tr>
              <td>Day 15</td>
              <td>
                An additional late fee of 5% (minimum $5) is added to the
                account.
              </td>
            </tr>
            <tr>
              <td>Day 21</td>
              <td>
                An additional late fee of 5% (minimum $5) is added to the
                account.
              </td>
            </tr>
            <tr>
              <td>Day 30</td>
              <td>
                We begin auction preparation by photographing the unit contents.
                If payment is made during this stage, you&apos;ll just need to
                replace the lock.
              </td>
            </tr>
            <tr>
              <td>Day 45</td>
              <td>
                A lien fee equal to one rent cycle will be added, and you’ll
                have until Day 89 to pay the full balance and retain your unit.
              </td>
            </tr>
            <tr>
              <td>Day 90</td>
              <td>
                If no payment has occurred, the unit is considered abandoned and
                the unit contents will be auctioned.
              </td>
            </tr>
          </tbody>
        </table>
      </>
    ),
    storingFoodQuestion: 'Storing food is not allowed',
    storingFoodAnswer: (
      <>
        All food and edible items—including boxed, canned, pet food, and
        spices—can attract pests. By avoiding these in storage, we all help
        maintain a clean, pest-free environment.
      </>
    ),
    unsafeItemsQuestion: 'Items unsafe to store',
    unsafeItemsAnswer: (
      <>
        For your safety and compliance, storing the following items is not
        allowed:
        <br />
        <ul>
          <li>
            Living things, such as plants or pets — and please don’t sleep or
            stay in your unit.
          </li>
          <li>
            Anything requiring power or your long-term presence inside the unit.
          </li>
          <li>
            Items that may leak or cause damage to your unit or others. If
            storing a vehicle, ground covering is recommended.
          </li>
          <li>
            Flammable, toxic, or hazardous materials, such as gas, propane, or
            chemicals.
          </li>
          <li>Illegal items, including weapons, drugs, or stolen goods.</li>
        </ul>
        Violating these rules may result in immediate termination of your rental
        agreement.
      </>
    ),
    fullTermsQuestion: 'Full terms of service',
    fullTermsAnswer: (
      <>
        While we take every precaution to maintain a safe and secure
        environment, we encourage you to review your rental terms for full
        details on responsibility and coverage.{' '}
        <a
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noreferrer"
          onClick={() => translations.en.track('Open terms of service')}
          href="https://www.depotium.com/en/terms-of-service/"
        >
          Terms of Service
        </a>
        .
      </>
    ),
    track: (eventName: string) => {
      /* placeholder; replaced by prop.track at runtime */
      // eslint-disable-next-line no-console
      console.log(`Track: ${eventName}`);
    },
  },

  fr: {
    faqTitle: 'FAQ',
    multipleChargesQuestion:
      'Pourquoi ma facture compte-t-elle plusieurs frais de location?',
    multipleChargesAnswer: (
      <>
        Votre compte est facturé toutes les quatre (4) semaines, à compter de
        votre date d’emménagement. Il est donc possible que votre relevé ou
        votre compte en ligne affiche des cycles de location à venir. Cela est
        tout à fait normal et vise à vous offrir une meilleure transparence.
        <br />
        <br />
        Si vous n’avez pas besoin de l’unité pour la totalité du cycle, nous
        pouvons calculer votre loyer au prorata, sur une base hebdomadaire.
        Veuillez nous joindre avant la prochaine échéance afin d’organiser votre
        paiement.
        <br />
        <br />
        Important : Vous n’avez qu’à régler le cycle de facturation en cours.
        Les frais relatifs aux futurs cycles affichés ne sont pas encore
        exigibles. Le paiement anticipé est possible, mais reste entièrement
        facultatif!
      </>
    ),
    accessQuestion: 'Quand puis-je accéder à mon unité?',
    accessAnswer: (scrollTo: (sectionId: string) => void) => (
      <>
        Les heures d’accès et/ou d’ouverture de la barrière sont indiquées dans
        la section{' '}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            scrollTo('contact');
            return false;
          }}
          style={{ textDecoration: 'none' }}
        >
          Contact
        </a>
        .
      </>
    ),
    storageTipsQuestion: 'Comment devrais-je entreposer mes articles?',
    storageTipsAnswer: (
      <>
        Voici quelques bonnes pratiques pour entreposer vos biens en toute
        confiance :
        <ul>
          <li>
            Étiquetez clairement vos boîtes pour retrouver facilement vos
            articles.
          </li>
          <li>
            Couvrez les matelas et textiles avec du plastique pour les protéger
            de la poussière et de l’humidité, mais ne les scellez jamais
            complètement. Laissez toujours une ouverture pour permettre la
            circulation de l’air.
          </li>
          <li>
            Assurez la sécurité de votre unité! Ne partagez jamais votre code
            d’accès ni votre clé.
          </li>
          <li>
            Notez le contenu de votre unité et rangez cette liste en lieu sûr.
          </li>
          <li>
            Si possible, évitez de poser vos articles directement sur le sol.
            Utilisez des palettes ou des tapis.
          </li>
        </ul>
      </>
    ),
    multipleUnitsQuestion: 'Que faire si j’ai plus d’une unité?',
    multipleUnitsAnswer: (
      sitelinkId: string,
      track: (eventName: string) => void
    ) => (
      <>
        Si vous louez plusieurs unités chez nous, elles peuvent toutes être
        consultées et gérées depuis un seul endroit.
        <br />
        <br />
        Consultez notre portail client sécurisé :{' '}
        <a
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noreferrer"
          onClick={() => track('Open MyAccount')}
          href={`https://www.smdservers.net/SLWebSiteTemplate_V2/login.aspx?sCorpCode=ilv2Y31A9yHEzplusa5/gLLlA==&sLocationCode=${sitelinkId}==&1=1`}
        >
          Mon compte
        </a>
        <br />
        Une fois connecté(e) :
        <ol>
          <li>Vous pourrez visualiser une liste de toutes vos unités.</li>
          <li>Sélectionnez l’une de vos unités.</li>
          <li>
            Suivez ensuite les mêmes étapes que celles indiquées dans les
            sections Paiements ou Accès.
          </li>
        </ol>
      </>
    ),
    referralsQuestion: 'Est-ce que Depotium a un programme de recommandation?',
    referralsAnswer: (
      <>
        Oui! Recommandez nos services à un proche et obtenez jusqu’à 100 $ grâce
        à notre programme de recommandation. Être un bon ami, c’est payant!
        <ul>
          <li>
            Admissibilité : les clients actuels, ainsi que les anciens clients,
            sont admissibles.
          </li>
          <li>
            Une fois que votre ami ou un proche aura emménagé dans son unité,
            nous communiquerons avec vous pour vous remettre votre récompense.
          </li>
          <li>
            Les primes de récompense sont remises sous forme de carte de crédit
            prépayée une fois que le nouveau client a emménagé dans l’unité et
            fourni tous les documents requis.
          </li>
          <li>
            Montant de la prime :
            <br />
            <table border={1} cellPadding={6} cellSpacing={0}>
              <thead>
                <tr>
                  <th style={{ minWidth: '4em' }}>Montant</th>
                  <th>Taille d’unité</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>50 $</td>
                  <td>0–100 pi² et stationnement extérieur</td>
                </tr>
                <tr>
                  <td>75 $</td>
                  <td>101–250 pi²</td>
                </tr>
                <tr>
                  <td>100 $</td>
                  <td>250 pi² et plus</td>
                </tr>
              </tbody>
            </table>
            <i>*Certaines conditions s’appliquent.</i>
          </li>
        </ul>
      </>
    ),
    movingOutQuestion:
      'Quelles sont les démarches à suivre lorsque je déménage?',
    movingOutAnswer: (
      <>
        Vous profitez de conditions de location flexibles!
        <br />
        <br />
        Si vous n’avez pas besoin de votre unité pour la totalité du cycle
        suivant, veuillez nous en informer avant le début du prochain cycle de
        facturation. Nous serons ravis de vous fournir une facture de location
        unique, calculée au prorata.
        <br />
        <br />
        Un remboursement est possible uniquement si l’unité est libérée avant la
        date d’échéance de la location. Veuillez emporter tous vos effets
        personnels lors de votre départ, retirer le cadenas de votre unité et
        informer notre équipe une fois que vous avez terminé. Des frais
        d’élimination, allant de 50 $ à 500 $, seront appliqués si des articles
        sont laissés sur place après le déménagement.
      </>
    ),
    protectionPlanQuestion: 'En quoi consiste le « plan de protection »?',
    protectionPlanAnswer: (
      <>
        La tranquillité d’esprit, c’est inclus!
        <br />
        <br />
        Toutes les locations d’unités comprennent automatiquement l’adhésion à
        notre plan de protection. Ce plan offre une protection limitée contre
        divers risques, comme les incendies, le vol et certains types de dégâts
        d’eau. Si vous avez déjà votre propre assurance, vous pouvez vous
        désinscrire du plan, en nous fournissant une preuve de couverture. Les
        protections offertes varient entre 2 500 $ et 5 000 $.
        <br />
        <br />
        Les réclamations doivent être soumises rapidement, accompagnées des
        documents nécessaires et des photos. Ce plan de protection ne remplace
        pas une assurance habitation ou une assurance locataire personnelle.
        <br />
        <br />
        Consultez tous les détails ici :{' '}
        <a
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noreferrer"
          onClick={() => translations.fr.track('Open terms of service')}
          href="https://tripemco.com/TripemcoSelfStorageWordings/TenantsWordingsLong.pdf#page=4"
        >
          Conditions d’utilisation
        </a>
      </>
    ),
    latePaymentQuestion: 'Que se passe-t-il en cas de retard de paiement?',
    latePaymentAnswer: (
      sitelinkId: string,
      track: (eventName: string) => void
    ) => (
      <>
        La meilleure façon de maintenir votre compte à jour est d’adhérer aux
        paiements automatiques. Vous pouvez vous inscrire par l’entremise de
        notre portail client sécurisé :{' '}
        <a
          style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
          target="_blank"
          rel="noreferrer"
          onClick={() => track('Open MyAccount')}
          href={`https://www.smdservers.net/SLWebSiteTemplate_V2/login.aspx?sCorpCode=ilv2Y31A9yHEzplusa5/gLLlA==&sLocationCode=${sitelinkId}==&1=1`}
        >
          Mon compte
        </a>
        <br />
        <br />
        <table border={1} cellPadding={6} cellSpacing={0}>
          <thead>
            <tr>
              <th style={{ minWidth: '4em' }}>Jour</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Jour 3</td>
              <td>
                Des frais de retard de 5 % (minimum 5 $) sont ajoutés au compte.
              </td>
            </tr>
            <tr>
              <td>Jour 5</td>
              <td>
                L’accès à l’unité et aux lieux est temporairement suspendu.
              </td>
            </tr>
            <tr>
              <td>Jour 9</td>
              <td>
                Des frais de retard supplémentaires de 5 % (minimum 5 $) sont
                ajoutés au compte.
              </td>
            </tr>
            <tr>
              <td>Jour 15</td>
              <td>
                Des frais de retard supplémentaires de 5 % (minimum 5 $) sont
                ajoutés au compte.
              </td>
            </tr>
            <tr>
              <td>Jour 21</td>
              <td>
                Des frais de retard supplémentaires de 5 % (minimum 5 $) sont
                ajoutés au compte.
              </td>
            </tr>
            <tr>
              <td>Jour 31</td>
              <td>
                Nous commençons à préparer la vente aux enchères en documentant
                par photo le contenu de l’unité. Si vous effectuez un paiement
                après cette étape, il vous suffira de remplacer le cadenas.
              </td>
            </tr>
            <tr>
              <td>Jour 45</td>
              <td>
                Des frais de privilège équivalents à un cycle de location seront
                ajoutés, et vous devrez régler le solde complet avant la date de
                la vente aux enchères pour conserver votre unité.
              </td>
            </tr>
            <tr>
              <td>Jour 90</td>
              <td>
                Si aucun paiement n’a été effectué, l’unité est considérée comme
                abandonnée et le contenu sera mis aux enchères.
              </td>
            </tr>
          </tbody>
        </table>
      </>
    ),
    storingFoodQuestion: 'Nourriture interdite dans les unités d’entreposage',
    storingFoodAnswer: (
      <>
        Tous les aliments et articles comestibles — y compris les boîtes, les
        conserves, la nourriture pour animaux et les épices — peuvent attirer
        des nuisibles. En évitant de stocker ces articles, nous contribuons tous
        à maintenir un environnement propre et sans parasites.
      </>
    ),
    unsafeItemsQuestion: (
      <>
        Y a-t-il des articles que je ne peux <u>pas</u> entreposer?
      </>
    ),
    unsafeItemsAnswer: (
      <>
        Tous les aliments et produits comestibles sont strictement interdits.
        Cela comprend, mais sans s’y limiter, les produits en boîte, en conserve
        ou périssables, la nourriture pour animaux et les épices. Ces articles
        peuvent attirer des nuisibles. En évitant d’entreposer ces produits,
        nous contribuons tous à maintenir un environnement propre et sans
        parasites.
        <br />
        <br />
        Par mesure de sécurité et par respect des règles de l’installation, il
        est interdit d’entreposer les articles suivants :<br />
        <br />
        <ul>
          <li>
            Les objets vivants ou organiques, comme les plantes, les graines, la
            terre, l’engrais, les animaux de compagnie et, bien entendu, les
            humains. (Veuillez ne pas dormir ni séjourner dans votre unité
            pendant de longues périodes.)
          </li>
          <li>
            Tout ce qui nécessite une alimentation électrique ou votre présence
            prolongée dans l’unité.
          </li>
          <li>
            Les articles pouvant causer des fuites ou des dommages à votre unité
            ou à celles des autres. Si vous entreposez un véhicule, il est
            recommandé d’utiliser une protection au sol.
          </li>
          <li>
            Les produits inflammables, toxiques ou dangereux, comme l’essence,
            le propane ou les produits chimiques.
          </li>
          <li>
            Les objets illégaux, y compris les armes, les drogues et les biens
            volés.
          </li>
        </ul>
        Le non-respect de ces règles peut entraîner la résiliation immédiate de
        votre entente d’utilisation.
      </>
    ),
    fullTermsQuestion: 'Conditions générales',
    fullTermsAnswer: (
      <>
        Bien que nous prenions toutes les précautions nécessaires pour maintenir
        un environnement sécuritaire, nous vous invitons à consulter les
        conditions relatives à l’entente d’utilisation pour connaître tous les
        détails concernant les responsabilités et la couverture.{' '}
        <a
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noreferrer"
          onClick={() => translations.fr.track('Open terms of service')}
          href="https://www.depotium.com/fr/terms-of-service/"
        >
          Conditions d’utilisation
        </a>
        .
      </>
    ),
    track: (eventName: string) => {
      /* placeholder; replaced by prop.track at runtime */
      // eslint-disable-next-line no-console
      console.log(`Track: ${eventName}`);
    },
  },
};

const ClientHubFaq: React.FC<ClientHubFaqProps> = ({
  isMobile,
  activeSection,
  track,
  scrollTo,
  sitelinkId,
  local,
}) => {
  const id = 'faq';
  const showSection = () => (isMobile ? activeSection === id : true);

  // pick the correct locale bundle, and override placeholder track:
  const t = { ...translations[local], track };

  const [expandMultipleCharges, setExpandMultipleCharges] = useState(false);
  const [expandAccessMyUnit, setExpandAccessMyUnit] = useState(false);
  const [expandStorageTips, setExpandStorageTips] = useState(false);
  const [expandReferrals, setExpandReferrals] = useState(false);
  const [expandFaqMultipleUnits, setExpandFaqMultipleUnits] = useState(false);
  const [expandMovingOut, setExpandMovingOut] = useState(false);
  const [expandProtectionPlan, setExpandProtectionPlan] = useState(false);
  const [expandLatePayment, setExpandLatePayment] = useState(false);
  const [expandItemsUnsafeToStore, setExpandItemsUnsafeToStore] =
    useState(false);
  const [expandFullTermsOfService, setExpandFullTermsOfService] =
    useState(false);

  return (
    <div style={{ display: showSection() ? 'block' : 'none' }}>
      <h3
        id="faq"
        style={{ textTransform: 'none', backgroundColor: 'aliceblue' }}
      >
        {t.faqTitle}
      </h3>

      {/* Referrals */}
      <div
        onClick={() => setExpandReferrals((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.referralsQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{ transform: expandReferrals ? 'rotateX(180deg)' : '' }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandReferrals && (
        <div>
          {t.referralsAnswer}
          <br />
        </div>
      )}

      {/* Access My Unit */}
      <div
        onClick={() => setExpandAccessMyUnit((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.accessQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{ transform: expandAccessMyUnit ? 'rotateX(180deg)' : '' }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandAccessMyUnit && (
        <div>
          {t.accessAnswer(scrollTo)}
          <br />
          <br />
        </div>
      )}

      {/* Storage Tips */}
      <div
        onClick={() => setExpandStorageTips((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.storageTipsQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{ transform: expandStorageTips ? 'rotateX(180deg)' : '' }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandStorageTips && (
        <div>
          {t.storageTipsAnswer}
          <br />
        </div>
      )}

      {/* Unsafe Items */}
      <div
        onClick={() => setExpandItemsUnsafeToStore((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.unsafeItemsQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{
              transform: expandItemsUnsafeToStore ? 'rotateX(180deg)' : '',
            }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandItemsUnsafeToStore && (
        <div>
          {t.unsafeItemsAnswer}
          <br />
          <br />
        </div>
      )}

      {/* Protection Plan */}
      <div
        onClick={() => setExpandProtectionPlan((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.protectionPlanQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{ transform: expandProtectionPlan ? 'rotateX(180deg)' : '' }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandProtectionPlan && (
        <div>
          {t.protectionPlanAnswer}
          <br />
          <br />
        </div>
      )}

      {/* Multiple charges */}
      <div
        onClick={() => setExpandMultipleCharges((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.multipleChargesQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{
              transform: expandMultipleCharges ? 'rotateX(180deg)' : '',
            }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandMultipleCharges && (
        <div>
          {t.multipleChargesAnswer}
          <br />
          <br />
        </div>
      )}

      {/* Late Payment */}
      <div
        onClick={() => setExpandLatePayment((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.latePaymentQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{ transform: expandLatePayment ? 'rotateX(180deg)' : '' }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandLatePayment && (
        <div>
          {t.latePaymentAnswer(sitelinkId, track)}
          <br />
          <br />
        </div>
      )}

      {/* Moving Out */}
      <div
        onClick={() => setExpandMovingOut((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.movingOutQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{ transform: expandMovingOut ? 'rotateX(180deg)' : '' }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandMovingOut && (
        <div>
          {t.movingOutAnswer}
          <br />
          <br />
        </div>
      )}

      {/* Multiple Units */}
      <div
        onClick={() => setExpandFaqMultipleUnits((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.multipleUnitsQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{
              transform: expandFaqMultipleUnits ? 'rotateX(180deg)' : '',
            }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandFaqMultipleUnits && (
        <div>
          {t.multipleUnitsAnswer(sitelinkId, track)}
          <br />
        </div>
      )}

      {/* Full Terms of Service */}
      <div
        onClick={() => setExpandFullTermsOfService((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <b>
          {t.fullTermsQuestion}{' '}
          <img
            src={chevronDown.src}
            style={{
              transform: expandFullTermsOfService ? 'rotateX(180deg)' : '',
            }}
            alt="chevron-down"
          />
        </b>
      </div>
      {expandFullTermsOfService && (
        <div>
          {t.fullTermsAnswer}
          <br />
          <br />
        </div>
      )}

      <hr />
    </div>
  );
};

export default ClientHubFaq;
